import React from "react";
import { useTranslation } from "react-i18next";
import { Download, Eye, X, Copy } from "lucide-react";
import { fmtMoney, fmtDateTime, groupIban } from "@/lib/format";
import { MoneyAmount } from "@/components/MoneyAmount";
import { authenticatedDownload, smartSaveOrShare } from "@/lib/download";
import { copyToClipboard } from "@/lib/clipboard";
import { usePdfViewer } from "@/lib/PdfViewerContext";
import { ReceiptPreview } from "@/components/DocumentPreview";
import { toast } from "sonner";

export default function TxDrawer({ tx, onClose }) {
  const { t } = useTranslation();
  const { openPdf } = usePdfViewer();
  if (!tx) return null;

  const isCredit = tx.amount > 0;
  const filename = `comprobante_${(tx.reference || tx.id).replace(/[^A-Za-z0-9_-]/g, "_")}.pdf`;
  const path = `/documents/receipt/${tx.id}`;

  const copy = async (txt) => {
    const ok = await copyToClipboard(txt);
    if (ok) toast.success(t("tx.drawer.copied"));
    else toast.error(t("tx.drawer.copy_error"));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-slate-900/50 backdrop-blur-sm"
      onClick={onClose}
      data-testid="tx-drawer"
    >
      <div
        className="w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-slate-100 flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="font-mono-fin text-xs uppercase tracking-widest text-blue-600">
              {tx.status === "scheduled" ? t("tx.drawer.scheduled") : t("tx.drawer.movement")}
            </div>
            <div className={`mt-2 font-display font-black text-3xl sm:text-4xl tracking-tighter break-words ${isCredit ? "text-green-600" : "text-slate-900"}`}>
              {isCredit ? "+" : ""}<MoneyAmount value={tx.amount} currency={tx.currency} decimalClassName="text-xl sm:text-2xl text-slate-400" />
            </div>
            {tx.requested_currency && tx.requested_currency !== (tx.currency || "EUR") && (
              <div className="text-[11px] text-slate-500 mt-1" data-testid="drawer-fx-note">
                {t("tx.drawer.requested")}: {fmtMoney(tx.requested_amount, tx.requested_currency)}
                {tx.fx_rate ? ` · 1 ${tx.requested_currency} = ${tx.fx_rate} ${tx.currency}` : ""}
              </div>
            )}
            <div className="text-slate-600 mt-1">{tx.description}</div>
          </div>
          <button onClick={onClose} data-testid="drawer-close" className="text-slate-400 hover:text-slate-900 p-2 -mt-2 -mr-2">
            <X size={20} />
          </button>
        </div>

        {/* Status badge */}
        <div className="px-6 pt-5">
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-mono-fin font-semibold ${
            tx.status === "completed" ? "bg-green-100 text-green-700" :
            tx.status === "scheduled" ? "bg-blue-100 text-blue-700" :
            tx.status === "pending" ? "bg-orange-100 text-orange-700" :
            tx.status === "rejected" ? "bg-red-100 text-red-700" :
            tx.status === "refunded" ? "bg-purple-100 text-purple-700" :
            "bg-slate-100 text-slate-700"
          }`} data-testid="drawer-status-badge">
            {tx.status === "completed" ? t("tx.drawer.status_completed")
              : tx.status === "scheduled" ? t("tx.drawer.status_scheduled")
              : tx.status === "pending" ? t("tx.drawer.status_pending")
              : tx.status === "rejected" ? t("tx.drawer.status_rejected")
              : tx.status === "refunded" ? t("tx.drawer.status_refunded")
              : tx.status}
          </span>
          {tx.status === "rejected" && tx.rejection_reason && (
            <div className="mt-3 border border-red-200 bg-red-50 rounded-xl px-3 py-2 text-xs text-red-800" data-testid="drawer-reject-reason">
              <b>{t("tx.drawer.reason")}:</b> {tx.rejection_reason}
            </div>
          )}
          {tx.status === "pending" && (
            <div className="mt-3 border border-orange-200 bg-orange-50 rounded-xl px-3 py-2 text-xs text-orange-900">
              {t("tx.drawer.pending_note")}
            </div>
          )}
          {tx.admin_note && (
            <div className="mt-3 border border-blue-200 bg-blue-50 rounded-xl px-3 py-2 text-xs text-blue-900" data-testid="drawer-admin-note">
              <b>{t("tx.drawer.note")}:</b> {tx.admin_note}
            </div>
          )}
        </div>

        {/* Details */}
        <dl className="p-6 space-y-3 text-sm">
          {[
            [t("tx.drawer.ref"), tx.reference, true],
            [t("tx.drawer.type"), tx.transfer_type ? t(`tx.type.${tx.transfer_type}`, { defaultValue: tx.transfer_type }) : "—", false],
            [t("tx.drawer.date"), fmtDateTime(tx.executed_at || tx.created_at), false],
            [t("tx.drawer.beneficiary"), tx.beneficiary_name || "—", false],
            [t("tx.drawer.destination"), tx.destination ? groupIban(tx.destination) : "—", !!tx.destination],
            [t("tx.drawer.concept"), tx.concept || "—", false],
          ].map(([k, v, copyable]) => (
            <div key={k} className="flex justify-between gap-3 border-b border-slate-100 pb-2 items-start">
              <dt className="text-slate-500 shrink-0">{k}</dt>
              <dd className="text-slate-900 text-right break-all font-mono-fin text-xs flex items-center gap-2 justify-end">
                <span>{v}</span>
                {copyable && v && v !== "—" && (
                  <button onClick={() => copy(v)} className="text-slate-400 hover:text-blue-600 shrink-0">
                    <Copy size={12} />
                  </button>
                )}
              </dd>
            </div>
          ))}
        </dl>

        {/* Actions */}
        <div className="p-6 border-t border-slate-100 space-y-3">
          <button
            onClick={() => openPdf({
              title: `${t("documents.receipt_doc_title")} ${tx.reference || ""}`.trim(),
              content: <ReceiptPreview tx={tx} />,
              path, filename,
            })}
            data-testid="drawer-view"
            className="w-full justify-center inline-flex items-center gap-2 bg-white text-slate-900 border border-slate-200 px-7 py-3.5 rounded-full font-semibold hover:border-blue-600 hover:text-blue-700 transition-colors"
          >
            <Eye size={16} /> {t("tx.drawer.view_receipt")}
          </button>
          <button
            onClick={() => smartSaveOrShare(path, filename)}
            data-testid="drawer-download"
            className="w-full justify-center inline-flex items-center gap-2 bg-blue-600 text-white px-7 py-3.5 rounded-full font-semibold hover:bg-slate-900 transition-colors"
          >
            <Download size={16} /> {t("tx.drawer.download_pdf")}
          </button>
          <p className="text-[11px] text-slate-500 text-center">
            {t("tx.drawer.footer_note")}
          </p>
        </div>
      </div>
    </div>
  );
}
